import React, { useContext, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal } from "antd";
import { setAddBulkTagsModalDisplay } from "@/features/modalSlice";
import { map } from "underscore";
import {
    IBulkRequiredConfig,
    IChannel,
    IMainContext,
    SubModule,
    TCommonRowData,
    TagsMap,
} from "@ts/common-types";
import { TAGS_MODULE_MAPPING, mainContext } from "@ts/constants";
import { getActiveModule, getGridTagsFormat } from "@ts/utils";
import { showToast } from "@ts/modals";
import { addEntityTags } from "@ts/retrieve-data";
import { setUpdateGridData } from "@ts/filters";
import BulkModalTagDropDown from "../drop-downs/tags/BulkModalTagDropDown";

type IBulkTagAdditionModalProps = {
    show: boolean;
};

type TSelectedRecords = {
    campaignsSelectionData?: {
        value: TCommonRowData;
    };
    productsSelectionData?: {
        value: TCommonRowData;
    };
};

const BulkTagAdditionModal: React.FC<IBulkTagAdditionModalProps> = ({
    show,
}) => {
    const { activeModule } = getActiveModule();
    const context = useContext<IMainContext>(mainContext);
    const dispatch = useDispatch();
    const handleClose = useCallback(() => {
        setSelectedTags([]);
        dispatch(setAddBulkTagsModalDisplay(false));
    }, [dispatch]);

    const [loading, setLoading] = useState<boolean>(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const channel = useSelector((state: IChannel) => state.channel.value);

    const {
        recordId,
        updateMethod,
        updateKey,
        updatePath,
        selectedData,
        colName,
        gridApi,
    } = TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];

    const selectedRecords = useSelector(
        (state: TSelectedRecords) =>
            state[selectedData as keyof TSelectedRecords].value
    );

    const handleAddTagsSubmit = useCallback(async () => {
        if (selectedTags.length === 0) {
            showToast("warning", "Please select at least one tag.");
            return;
        }

        setLoading(true);
        try {
            const tagIds = map(selectedTags, (tag: string) =>
                parseInt(tag.split("##")[0], 10)
            );

            const recordIds =
                activeModule === SubModule.CAMPAIGN
                    ? map(
                          selectedRecords,
                          (record) => record[recordId] as string | number
                      )
                    : map(selectedRecords, (record) => ({
                          sku: record[recordId] as string,
                          channel,
                      }));

            const response = await addEntityTags(
                recordIds,
                updateMethod,
                updatePath,
                updateKey,
                tagIds,
                true
            );

            if (!response.status) {
                throw new Error(response.message);
            } else {
                const updatedTags = getGridTagsFormat(tagIds);
                const bulkRequiredConfig: IBulkRequiredConfig = {
                    selectedRows: selectedRecords,
                    paramToFilter: recordId,
                    bulkParamsToChange: [
                        {
                            key: colName,
                            shouldArrayPreserve: true,
                            newValue: updatedTags,
                        },
                    ],
                };

                setUpdateGridData({
                    isReduxState: true,
                    bulkRequiredConfig,
                });
            }

            showToast("success", `Tags added successfully`);
            handleClose();
        } catch (err: any) {
            console.error(err.message);
            showToast(
                "danger",
                `Unable to update tags ${err?.message && ": " + err?.message}`
            );
        } finally {
            setLoading(false);
            if (gridApi) {
                context[gridApi].deselectAll();
            }
        }
    }, [
        selectedTags,
        selectedRecords,
        recordId,
        updateMethod,
        updatePath,
        updateKey,
        colName,
        gridApi,
        context,
        handleClose,
        activeModule,
        channel,
    ]);

    return (
        <Modal
            open={show}
            centered
            title={
                <div
                    className="d-flex align-items-center pb-3"
                    style={{ fontSize: "1.5rem", fontWeight: "600" }}
                >
                    Add Tags
                </div>
            }
            onCancel={handleClose}
            footer={[
                <Button key="back" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleAddTagsSubmit}
                    disabled={selectedTags.length === 0}
                >
                    Submit
                </Button>,
            ]}
            width="630px"
            styles={{
                body: {
                    height: "130px",
                },
            }}
            destroyOnHidden={true}
        >
            <div
                className="d-flex align-items-center justify-content-center"
                style={{
                    width: "100%",
                    height: "100%",
                }}
            >
                <BulkModalTagDropDown
                    setSelectedTags={setSelectedTags}
                    selectedTags={selectedTags}
                />
            </div>
        </Modal>
    );
};

const MemoizedBulkTagAdditionModal = React.memo(BulkTagAdditionModal);
export default MemoizedBulkTagAdditionModal;
